import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import Image from "next/image";
import Link from "next/link";

import {
  formatSummitDate,
  localizeSummitMessage,
  summitBreakdownDays,
} from "@/components/home/summit-breakdowns-data";
import { getVideoThumbnail } from "@/lib/youtube";
import type { Locale } from "@/i18n";

export type SummitBreakdownsTranslations = {
  title: string;
  subtitle: string;
  readBreakdown: string;
  dayLabel: string;
};

const defaultTranslations: SummitBreakdownsTranslations = {
  title: "The Word from Each Day",
  subtitle: "Message breakdowns from every Youth Revival Summit 2026 service",
  readBreakdown: "Read the breakdown",
  dayLabel: "Day {n}",
};

type SummitBreakdownsSectionProps = {
  locale: Locale;
  translations?: SummitBreakdownsTranslations;
};

export function SummitBreakdownsSection({
  locale,
  translations = defaultTranslations,
}: SummitBreakdownsSectionProps) {
  const days = summitBreakdownDays.filter((day) => day.messages.length > 0);
  if (days.length === 0) return null;

  const t = translations;

  return (
    <section
      id="summit-breakdowns"
      aria-label={t.title}
      className="border-b border-zinc-200 bg-white py-16 transition-colors duration-500 dark:border-white/5 dark:bg-zinc-950 md:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-10 md:mb-14">
          <h2 className="font-serif text-4xl leading-[0.95] tracking-tight text-black dark:text-white md:text-6xl">
            {t.title}
          </h2>
          <p className="mt-4 max-w-xl text-zinc-500 dark:text-zinc-400">
            {t.subtitle}
          </p>
        </div>

        <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 xl:grid-cols-5">
          {days.map((day) => {
            const firstMessage = day.messages[0];
            return (
              <Link
                key={day.day}
                href={`/${locale}/summit/day-${day.day}`}
                className="group block outline-none ring-brand-red/40 focus-visible:ring-2 dark:ring-brand-gold/40"
              >
                <div className="relative aspect-video overflow-hidden rounded-2xl bg-zinc-200 dark:bg-zinc-800">
                  <Image
                    src={getVideoThumbnail(firstMessage.videoId, "high")}
                    alt={`Youth Revival Summit 2026 ${day.label}`}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 340px"
                    className="object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>

                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <p className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-brand-red dark:text-brand-gold">
                    {t.dayLabel.replace("{n}", String(day.day))}
                  </p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {formatSummitDate(day.date, locale)}
                  </p>
                </div>

                <div className="mt-2 space-y-3">
                  {day.messages.map((message) => {
                    const local = localizeSummitMessage(message, locale);
                    return (
                      <div key={message.slug}>
                        <h3 className="font-serif text-xl leading-snug text-black dark:text-white">
                          {local.title}
                        </h3>
                        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                          {local.preacher}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-red transition-colors group-hover:text-black dark:text-brand-gold dark:group-hover:text-white">
                  {t.readBreakdown}
                  <ArrowRight
                    size={14}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
